import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Auth0ProviderWithHistory from './auth/Auth0ProviderWithHistory';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Calendar from './pages/Calendar';
import AccountDetail from './pages/AccountDetail';
import Admin from './pages/Admin';
import Quotes from './pages/Quotes';
import QuoteForm from './pages/QuoteForm';
import QuoteDetail from './pages/QuoteDetail';
import ImplementationPlan from './pages/ImplementationPlan';
import { MeProvider, useMe } from './hooks/useMe';
import NoAccess from './pages/NoAccess';
import { I18nProvider, useI18n } from './i18n';

function AccessGate({ children }) {
  const { me, loading, error, reload } = useMe();
  const { t } = useI18n();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Un fallo de red no es una negativa de acceso: se muestra como error, con
  // reintento, en vez de mandar al usuario a la pantalla de bloqueo.
  if (error) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', gap: 2, p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 420 }}>{error}</Alert>
        <Button variant="outlined" onClick={reload}>{t('common.retry')}</Button>
      </Box>
    );
  }

  if (!me?.access_granted) return <NoAccess />;
  return children;
}

function ProtectedRoute({ component: Component }) {
  const Guarded = () => (
    <AccessGate>
      <Component />
    </AccessGate>
  );
  const Protected = withAuthenticationRequired(Guarded, {
    onRedirecting: () => (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    ),
  });
  return <Protected />;
}

function RootRedirect() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('code') && params.get('state')) return null;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/dashboard" element={<ProtectedRoute component={Dashboard} />} />
      <Route path="/accounts" element={<ProtectedRoute component={Accounts} />} />
      <Route path="/calendar" element={<ProtectedRoute component={Calendar} />} />
      <Route path="/admin" element={<ProtectedRoute component={Admin} />} />
      <Route path="/quotes" element={<ProtectedRoute component={Quotes} />} />
      <Route path="/quotes/new" element={<ProtectedRoute component={QuoteForm} />} />
      <Route path="/quotes/:id" element={<ProtectedRoute component={QuoteDetail} />} />
      <Route path="/quotes/:id/edit" element={<ProtectedRoute component={QuoteForm} />} />
      <Route path="/accounts/:tenantId" element={<ProtectedRoute component={AccountDetail} />} />
      <Route path="/accounts/:tenantId/implementation" element={<ProtectedRoute component={ImplementationPlan} />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <I18nProvider>
        <BrowserRouter>
          <Auth0ProviderWithHistory>
            <MeProvider>
              <AppRoutes />
            </MeProvider>
          </Auth0ProviderWithHistory>
        </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  );
}
